import { PersonProfilesFormMethods } from "./profile.types"
import { useState, useEffect } from "react"
import { LotacaoService } from "@/modules/business/lotacoes/lotacao.service"
import { ReadLotacaoDto } from "@/api.types"
import { SearchDtoLotacoes } from "@/shared/types/search.dto"
import { DataAmigavel } from "@/shared/utils/datas"
import { Tabs, Tab } from "react-bootstrap"
import { Controller, useFieldArray, useFormState } from "react-hook-form"
import { FieldError, formSelectClass, getNestedFieldError } from "@/shared/components/forms/FormFieldFeedback"
import { PersonFormValues } from "@/modules/people/presentation/person.schema"
import { readFuncao } from "@/modules/structure/funcao/funcao.service"
import AdicionarAutorizacaoTrabalhoRemotoModal from "../../modals/AdicionarAutorizacaoTrabalhoRemotoModal"
import AdicionarVinculoAssessorModal from "../../modals/AdicionarVinculoAssessorModal"
import {
  AutorizacaoTrabalhoRemotoFormDto,
  PersonFormDto,
  VinculoAssessorDefensorFormDto,
} from "@/modules/people/presentation/person-form.utils"
import { BotaoAcaoPerfil } from "../../components/BotaoAcaoPerfil"
import { LotacoesPerfilTable } from "./lotacoes-perfil-table"
import { EstagiariosResponsavelLista } from "./estagiarios-responsavel-lista"

interface DefensorProfileFieldsProps {
  methods: PersonProfilesFormMethods
}

/** Conteúdo da aba Assessores — montado só ao abrir a tab (`mountOnEnter`). */
function AssessoresVinculosTab({
  methods,
  vinculos,
  appendVinculo,
  removeVinculo,
}: {
  methods: PersonProfilesFormMethods
  vinculos: VinculoAssessorDefensorFormDto[]
  appendVinculo: ReturnType<typeof useFieldArray<PersonFormDto, 'defensor.vinculosAssessores'>>['append']
  removeVinculo: ReturnType<typeof useFieldArray<PersonFormDto, 'defensor.vinculosAssessores'>>['remove']
}) {
  const [modalVinculoShow, setModalVinculoShow] = useState(false)
  const [indexEdicaoVinculo, setIndexEdicaoVinculo] = useState(-1)

  const abrirNovoVinculo = () => {
    setIndexEdicaoVinculo(-1)
    setModalVinculoShow(true)
  }

  const abrirEditarVinculo = (index: number) => {
    const item = vinculos?.[index]
    if (!item) return
    setIndexEdicaoVinculo(index)
    setModalVinculoShow(true)
  }

  const vinculoVazio: VinculoAssessorDefensorFormDto = {
    colaborador_id: undefined,
    colaborador_nome: null,
    data_inicial: '',
    data_final: null,
  }
  const initialVinculo: VinculoAssessorDefensorFormDto =
    indexEdicaoVinculo >= 0
      ? (vinculos?.[indexEdicaoVinculo] ?? vinculoVazio)
      : vinculoVazio

  return (
    <>
      <div className="d-flex justify-content-end mb-2">
        <BotaoAcaoPerfil tipo="adicionar" onClick={abrirNovoVinculo}>
          Adicionar assessor
        </BotaoAcaoPerfil>
      </div>
      {vinculos && vinculos.length > 0 ? (
        <table className="table table-sm table-bordered table-hover table-striped mb-0">
          <thead>
            <tr>
              <th>Assessor</th>
              <th className="text-center">Início</th>
              <th className="text-center">Fim</th>
              <th className="text-end">Ações</th>
            </tr>
          </thead>
          <tbody>
            {vinculos.map((item, index) => (
              <tr key={(item as VinculoAssessorDefensorFormDto & { _id?: string })._id ?? item.id ?? index}>
                <td className="text-break">
                  {item.colaborador_nome?.trim()
                    ? item.colaborador_nome
                    : item.colaborador_id
                      ? `#${item.colaborador_id}`
                      : '-'}
                </td>
                <td className="text-center">
                  {item.data_inicial ? DataAmigavel(item.data_inicial) : '-'}
                </td>
                <td className="text-center">
                  {item.data_final ? DataAmigavel(item.data_final) : 'Em aberto'}
                </td>
                <td className="text-end">
                  <BotaoAcaoPerfil tipo="editar" onClick={() => abrirEditarVinculo(index)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-muted small mb-0 py-1">Nenhum assessor vinculado a este defensor.</p>
      )}

      <AdicionarVinculoAssessorModal
        show={modalVinculoShow}
        handleHide={() => setModalVinculoShow(false)}
        initialData={initialVinculo}
        itemIndex={indexEdicaoVinculo}
        existentes={(vinculos ?? []) as VinculoAssessorDefensorFormDto[]}
        appendItem={appendVinculo}
        setValue={methods.setValue}
        onExcluir={
          indexEdicaoVinculo >= 0
            ? () => removeVinculo(indexEdicaoVinculo)
            : undefined
        }
      />
    </>
  )
}

export function DefensorProfileFields({ methods }: DefensorProfileFieldsProps) {
  const { errors } = useFormState<PersonFormValues>();
  const defensorId = methods.watch('defensor.id')
  const pessoaId = methods.watch('id')
  const [funcoes, setFuncoes] = useState<{ id: number; nome: string }[]>([])
  const [lotacoesAtuais, setLotacoesAtuais] = useState<ReadLotacaoDto[]>([])
  const [modalAutorizacaoShow, setModalAutorizacaoShow] = useState(false)
  const [indexEdicao, setIndexEdicao] = useState(-1)

  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: 'defensor.autorizacoesTrabalhoRemoto',
    keyName: '_id',
  })

  const {
    fields: camposVinculos,
    append: appendVinculo,
    remove: removeVinculo,
  } = useFieldArray({
    control: methods.control,
    name: 'defensor.vinculosAssessores',
    keyName: '_id',
  })

  const autorizacoes = methods.watch('defensor.autorizacoesTrabalhoRemoto') ?? fields
  const vinculos = (methods.watch('defensor.vinculosAssessores') ?? camposVinculos) as VinculoAssessorDefensorFormDto[]
  const funcaoError = getNestedFieldError(errors, 'defensor.funcao')
    ?? getNestedFieldError(errors, 'defensor.funcao.id')

  useEffect(() => {
    readFuncao({ take: 1000, orderBy: 'ASC', orderField: 'nome' })
      .then(([data]) => setFuncoes(data))
      .catch(() => setFuncoes([]))
  }, [])

  const recarregarLotacoes = () => {
    if (!defensorId) {
      setLotacoesAtuais([]);
      return;
    }
    const lotacaoService = new LotacaoService();
    const searchParams: SearchDtoLotacoes = {
      skip: 0,
      take: 9000,
      id_defensor: defensorId,
    } as SearchDtoLotacoes;

    lotacaoService
      .Enumerate(searchParams)
      .then((result) => {
        setLotacoesAtuais(result[0]);
      })
      .catch((error) => {
        console.warn('Erro ao carregar lotações:', error);
      });
  };

  useEffect(() => {
    recarregarLotacoes();
  }, [defensorId]);

  const abrirNovaAutorizacao = () => {
    setIndexEdicao(-1)
    setModalAutorizacaoShow(true)
  }

  const abrirEditarAutorizacao = (index: number) => {
    const item = autorizacoes?.[index]
    if (!item) return
    setIndexEdicao(index)
    setModalAutorizacaoShow(true)
  }

  const autorizacaoVazia: AutorizacaoTrabalhoRemotoFormDto = {
    data_inicial: '',
    data_final: null,
    anotacao: null,
  }
  const initialAutorizacao: AutorizacaoTrabalhoRemotoFormDto =
    indexEdicao >= 0
      ? (autorizacoes?.[indexEdicao] ?? autorizacaoVazia)
      : autorizacaoVazia

  return (
    <>
      <div className="row align-items-start g-3">

        <div className="row g-2">
          <div className="col-md-2">
            <label htmlFor="defensor_titulo" className="form-label mb-1">Título:</label>
            <input
              id="defensor_titulo"
              type="text"
              className="form-control form-control-sm"
              {...methods.register('defensor.titulo')}
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="defensor_oab" className="form-label mb-1">Número OAB:</label>
            <input
              id="defensor_oab"
              type="text"
              className="form-control form-control-sm"
              {...methods.register('defensor.numero_oab')}
            />
          </div>

          <div className="col-md-2">
            <label htmlFor="defensor_matricula" className="form-label mb-1">Matrícula:</label>
            <input
              id="defensor_matricula"
              type="text"
              className="form-control form-control-sm"
              {...methods.register('defensor.matricula_defensoria')}
            />
          </div>

          <div className="col-md-6">
            <label htmlFor="defensor_funcao" className="form-label mb-1">Função:</label>
            <Controller
              name="defensor.funcao.id"
              control={methods.control}
              render={({ field }) => (
                <select
                  id="defensor_funcao"
                  className={`${formSelectClass(!!funcaoError)} form-select-sm`}
                  value={field.value || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    field.onChange(value === '' ? null : Number(value));
                  }}
                  onBlur={field.onBlur}
                >
                  <option value="">Selecione uma função</option>
                  {funcoes.map((funcao) => (
                    <option key={funcao.id} value={funcao.id}>{funcao.nome}</option>
                  ))}
                </select>
              )}
            />
            <FieldError message={funcaoError?.message} />
          </div>

          <div className="col-md-3">
            <label htmlFor="defensor_data_admissao" className="form-label mb-1 small">Admissão:</label>
            <input
              id="defensor_data_admissao"
              type="date"
              className="form-control form-control-sm"
              {...methods.register('defensor.data_admissao')}
            />
          </div>
          <div className="col-md-3">
            <label htmlFor="defensor_data_referencia" className="form-label mb-1 small">Referência:</label>
            <input
              id="defensor_data_referencia"
              type="date"
              className="form-control form-control-sm"
              {...methods.register('defensor.data_referencia')}
            />
          </div>

          <div className="col-md-9 mt-3">
            <label htmlFor="defensor_anotacao" className="form-label mb-1">Anotações:</label>
            <input
              id="defensor_anotacao"
              type="text"
              className="form-control form-control-sm"
              {...methods.register('defensor.anotacao')}
            />
          </div>
          <div className="col-md-3 mt-3">
            <label htmlFor="defensor_data_exoneracao" className="form-label mb-1 small">Exoneração:</label>
            <input
              id="defensor_data_exoneracao"
              type="date"
              className="form-control form-control-sm"
              {...methods.register('defensor.data_exoneracao')}
            />
          </div>
        </div>
      </div>

      <div className="mt-3">
        <Tabs
          defaultActiveKey="lotacoes"
          id="defensor-perfil-tabs"
          className="mb-2"
          mountOnEnter
          unmountOnExit
        >
          <Tab eventKey="lotacoes" title="Lotações">
            <LotacoesPerfilTable
              lotacoes={lotacoesAtuais}
              emptyMessage="Nenhuma lotação cadastrada para este defensor."
              tipoPessoa="defensor"
              pessoaLotadaId={defensorId}
              onRecarregar={recarregarLotacoes}
            />
          </Tab>

          <Tab eventKey="assessores" title="Assessores">
            <AssessoresVinculosTab
              methods={methods}
              vinculos={vinculos}
              appendVinculo={appendVinculo}
              removeVinculo={removeVinculo}
            />
          </Tab>

          <Tab eventKey="estagiarios" title="Estagiários">
            <EstagiariosResponsavelLista
              pessoaId={pessoaId}
              emptyMessage="Nenhum estagiário com lotação vigente sob responsabilidade deste defensor."
            />
          </Tab>

          <Tab eventKey="trabalho-remoto" title="Trabalho remoto">
            <div className="d-flex justify-content-end mb-2">
              <BotaoAcaoPerfil tipo="adicionar" onClick={abrirNovaAutorizacao}>
                Adicionar autorização
              </BotaoAcaoPerfil>
            </div>
            {autorizacoes && autorizacoes.length > 0 ? (
              <table className="table table-sm table-bordered table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th className="text-center">Data inicial</th>
                    <th className="text-center">Data final</th>
                    <th>Anotação</th>
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {autorizacoes.map((item, index) => (
                    <tr key={(item as AutorizacaoTrabalhoRemotoFormDto & { _id?: string })._id ?? item.id ?? index}>
                      <td className="text-center">
                        {item.data_inicial ? DataAmigavel(item.data_inicial) : '-'}
                      </td>
                      <td className="text-center">
                        {item.data_final ? DataAmigavel(item.data_final) : 'Em aberto'}
                      </td>
                      <td className="text-break">
                        {item.anotacao?.trim() ? item.anotacao : '-'}
                      </td>
                      <td className="text-end">
                        <BotaoAcaoPerfil tipo="editar" onClick={() => abrirEditarAutorizacao(index)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="text-muted small mb-0 py-1">Nenhuma autorização de trabalho remoto cadastrada.</p>
            )}
          </Tab>
        </Tabs>
      </div>

      <AdicionarAutorizacaoTrabalhoRemotoModal
        show={modalAutorizacaoShow}
        handleHide={() => setModalAutorizacaoShow(false)}
        initialData={initialAutorizacao}
        itemIndex={indexEdicao}
        existentes={(autorizacoes ?? []) as AutorizacaoTrabalhoRemotoFormDto[]}
        appendItem={append}
        setValue={methods.setValue}
        onExcluir={indexEdicao >= 0 ? () => remove(indexEdicao) : undefined}
      />
    </>
  )
}

/** @deprecated Use OperationalProfilesSection */
export function DefensorProfile({
  methods,
  podeVerDefensor,
}: {
  methods: PersonProfilesFormMethods;
  podeVerDefensor: boolean;
}) {
  if (!podeVerDefensor) return null;
  return <DefensorProfileFields methods={methods} />;
}
